import { WhiteboardPayload, WhiteboardPoint, WhiteboardStroke } from '@sos/shared';

export interface VirtualWhiteboardModalOptions {
  snapshotDataUrl: string;
  onSendAnnotation: (payload: WhiteboardPayload) => void;
  onClose: () => void;
}

export class VirtualWhiteboardModal {
  private element: HTMLElement;
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private strokes: WhiteboardStroke[] = [];
  private currentStroke: WhiteboardPoint[] = [];
  private isDrawing: boolean = false;

  private currentColor: string = '#ef4444';
  private currentWidth: number = 5;

  constructor(private readonly options: VirtualWhiteboardModalOptions) {
    this.element = document.createElement('div');
    this.element.className = 'whiteboard-editor-modal';
    this.render();
  }

  private render(): void {
    this.element.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; flex-shrink:0;">
        <div style="display:flex; align-items:center; gap:10px;">
          <div style="background:rgba(239,68,68,0.2); border:1px solid #ef4444; border-radius:8px; padding:6px; color:#ef4444;">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m12 19 7-7 3 3-7 7-3-3z"></path><path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path><path d="m2 2 7.586 7.586"></path><circle cx="11" cy="11" r="2"></circle></svg>
          </div>
          <div>
            <h2 style="font-size:1.1rem; font-weight:800;">Pizarra Virtual sobre Captura Clínica</h2>
            <p style="font-size:0.8rem; color:var(--text-muted);">Dibuje indicaciones anatómicas para proyectarlas al ciudadano</p>
          </div>
        </div>

        <!-- Paleta de Herramientas -->
        <div style="display:flex; align-items:center; gap:12px;">
          <div style="display:flex; gap:6px;">
            <button class="color-btn" data-color="#ef4444" style="background:#ef4444; width:28px; height:28px; border-radius:50%; border:2px solid #fff; cursor:pointer;"></button>
            <button class="color-btn" data-color="#f59e0b" style="background:#f59e0b; width:28px; height:28px; border-radius:50%; border:2px solid transparent; cursor:pointer;"></button>
            <button class="color-btn" data-color="#10b981" style="background:#10b981; width:28px; height:28px; border-radius:50%; border:2px solid transparent; cursor:pointer;"></button>
            <button class="color-btn" data-color="#06b6d4" style="background:#06b6d4; width:28px; height:28px; border-radius:50%; border:2px solid transparent; cursor:pointer;"></button>
          </div>

          <button id="btn-undo-stroke" class="btn-ghost">↩ Deshacer</button>
          <button id="btn-clear-canvas" class="btn-ghost">🗑 Limpiar</button>
          <button id="btn-transmit-annotation" class="btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            TRANSMITIR AL CIUDADANO
          </button>
          <button id="btn-close-wb" class="btn-ghost" style="font-size:1.2rem;">✕</button>
        </div>
      </div>

      <div class="whiteboard-canvas-holder">
        <canvas id="editor-canvas"></canvas>
      </div>
    `;

    this.canvas = this.element.querySelector('#editor-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    this.loadSnapshotBackground();
    this.setupDrawingEvents();

    this.element.querySelector('#btn-close-wb')?.addEventListener('click', () => this.close());
    this.element.querySelector('#btn-clear-canvas')?.addEventListener('click', () => this.clear());
    this.element.querySelector('#btn-undo-stroke')?.addEventListener('click', () => this.undo());
    this.element.querySelector('#btn-transmit-annotation')?.addEventListener('click', () => this.transmit());

    this.element.querySelectorAll('.color-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        this.element.querySelectorAll('.color-btn').forEach((b) => ((b as HTMLElement).style.borderColor = 'transparent'));
        target.style.borderColor = '#fff';
        this.currentColor = target.getAttribute('data-color') || '#ef4444';
      });
    });
  }

  private loadSnapshotBackground(): void {
    const img = new Image();
    img.src = this.options.snapshotDataUrl;
    img.onload = () => {
      // Ajustar dimensiones del canvas manteniendo relación de aspecto
      const maxW = window.innerWidth - 60;
      const maxH = window.innerHeight - 140;

      let w = img.width;
      let h = img.height;

      const ratio = Math.min(maxW / w, maxH / h);
      this.canvas.width = Math.round(w * ratio);
      this.canvas.height = Math.round(h * ratio);

      this.redraw();
    };
  }

  private setupDrawingEvents(): void {
    const getPos = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
      const rect = this.canvas.getBoundingClientRect();
      const clientX = 'touches' in e && e.touches[0] ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e && e.touches[0] ? e.touches[0].clientY : (e as MouseEvent).clientY;
      return {
        x: Math.max(0, Math.min(1, (clientX - rect.left) / this.canvas.width)),
        y: Math.max(0, Math.min(1, (clientY - rect.top) / this.canvas.height))
      };
    };

    const start = (e: MouseEvent | TouchEvent) => {
      this.isDrawing = true;
      this.currentStroke = [getPos(e)];
    };

    const move = (e: MouseEvent | TouchEvent) => {
      if (!this.isDrawing) return;
      const pt = getPos(e);
      this.currentStroke.push(pt);
      this.redraw();
    };

    const end = () => {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      if (this.currentStroke.length > 0) {
        this.strokes.push({
          points: [...this.currentStroke],
          color: this.currentColor,
          lineWidth: this.currentWidth
        });
        this.currentStroke = [];
        this.redraw();
      }
    };

    this.canvas.addEventListener('mousedown', start);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);

    this.canvas.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', end);
  }

  private redraw(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Dibujar imagen de fondo
    const img = new Image();
    img.src = this.options.snapshotDataUrl;
    this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);

    // Dibujar trazos guardados
    const allStrokes = [...this.strokes];
    if (this.currentStroke.length > 0) {
      allStrokes.push({
        points: this.currentStroke,
        color: this.currentColor,
        lineWidth: this.currentWidth
      });
    }

    const w = this.canvas.width;
    const h = this.canvas.height;

    allStrokes.forEach((stroke) => {
      if (stroke.points.length < 1) return;
      this.ctx.beginPath();
      this.ctx.strokeStyle = stroke.color;
      this.ctx.lineWidth = stroke.lineWidth;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';

      const first = stroke.points[0];
      if (first) {
        this.ctx.moveTo(first.x * w, first.y * h);
      }

      for (let i = 1; i < stroke.points.length; i++) {
        const pt = stroke.points[i];
        if (pt) {
          this.ctx.lineTo(pt.x * w, pt.y * h);
        }
      }

      this.ctx.stroke();
    });
  }

  private undo(): void {
    this.strokes.pop();
    this.redraw();
  }

  private clear(): void {
    this.strokes = [];
    this.currentStroke = [];
    this.redraw();
  }

  private transmit(): void {
    let annotatedBase64 = '';
    try {
      annotatedBase64 = this.canvas.toDataURL('image/jpeg', 0.9);
    } catch (_) {}

    const payload: WhiteboardPayload = {
      snapshotId: `snap_${Date.now()}`,
      snapshotBase64: annotatedBase64 || this.options.snapshotDataUrl,
      strokes: this.strokes,
      timestamp: Date.now()
    };
    this.options.onSendAnnotation(payload);
    this.close();
  }

  public close(): void {
    this.element.remove();
    this.options.onClose();
  }

  public mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }
}
